/**
 * Admin Installs RPC definitions
 *
 * Admin endpoints for the Installs admin UI — requires admin role.
 * Read RPCs (list + detail) and a create-install action that
 * dispatches the VPS provisioning workflow.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { AgentChannelName } from "@fm/db/models/AgentChannel";
import {
  AgentInstallationStatus,
  BrowserKind,
  DeploymentType,
  InstallFeeStatus,
  OcAssessmentSummary,
  OcHealthSnapshot,
} from "@fm/db/models/AgentInstallation";
import { SubscriptionStatus } from "@fm/db/models/Subscription";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import {
  DatabaseError,
  Forbidden,
  InvalidStateTransition,
  OpenclawUpgradeRefused,
  RecordNotFoundError,
  RemoteSupportNotEnabled,
  Unauthorized,
  ValidationError,
  ValkeyError,
} from "../errors.ts";

// ============================================================================
// Errors
// ============================================================================

/**
 * Closed union of best-effort failure tags accumulated during cloud-vps
 * deprovision. Each tag corresponds to one step the
 * {@link VpsDeprovisioner} attempts; the orchestrator collects every
 * tag it observes into a `ReadonlyArray<DeprovisionErrorTag>` so the
 * admin RPC layer (CLA-267) can surface partial-failure detail.
 *
 * The union is intentionally closed — sibling tear-down services
 * (e.g. Mac, CLA-269) and the VPS destroyer share this single literal
 * union so the orchestrator's error-collection type stays unified.
 *
 * @since 1.0.0
 */
export const DeprovisionErrorTag = Schema.Literal(
  "mac-unreachable",
  "frp-token-clear",
  "hetzner-server-delete",
  "hetzner-server-id-missing",
  "digitalocean-server-delete",
  "digitalocean-server-id-missing",
  "ovh-server-delete",
  "ovh-server-id-missing",
  "do-dns-delete",
  "do-dns-record-id-missing",
  "discord-bot-release",
);
export type DeprovisionErrorTag = Schema.Schema.Type<typeof DeprovisionErrorTag>;

/**
 * Returned by `deprovision` when the install row's status already sits
 * at a terminal value (`deprovisioned` is handled idempotently — this
 * error fires for `deleted`). Distinct from `InvalidStateTransition`
 * so the admin UI can render copy specific to the deprovision flow.
 *
 * @since 1.0.0
 */
export class InstallNotDeprovisionable extends Schema.TaggedError<InstallNotDeprovisionable>()(
  "InstallNotDeprovisionable",
  {
    installationId: Schema.String,
    status: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Returned by `deprovision` when one or more best-effort sub-steps
 * (Mac uninstall ack, Hetzner delete, DO DNS delete, frp token clear)
 * failed but the row was still advanced to `deprovisioned`. The
 * `errors` array surfaces every failed sub-step so the admin UI can
 * render the closed `DeprovisionErrorTag` literals directly.
 *
 * @since 1.0.0
 */
export class DeprovisionPartialFailure extends Schema.TaggedError<DeprovisionPartialFailure>()(
  "DeprovisionPartialFailure",
  {
    installationId: Schema.String,
    errors: Schema.Array(DeprovisionErrorTag),
    message: Schema.String,
  },
) {}

/**
 * Returned by `create` when an install already exists for the given
 * (customerId, name) pair. The admin UI surfaces this as a friendly
 * message rather than a generic database error.
 *
 * @since 1.0.0
 */
export class InstallAlreadyProvisionedError extends Schema.TaggedError<InstallAlreadyProvisionedError>()(
  "InstallAlreadyProvisionedError",
  {
    customerId: Schema.String,
    name: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Surfaced by `hetznerOptions` when the upstream Hetzner Cloud API
 * fails (network error, non-2xx, malformed body). The admin form
 * renders the message as a generic "couldn't load options" banner.
 *
 * @since 1.0.0
 */
export class HetznerCatalogRpcError extends Schema.TaggedError<HetznerCatalogRpcError>()(
  "HetznerCatalogRpcError",
  {
    message: Schema.String,
  },
) {}

/**
 * Returned by `provision` when the underlying VPS workflow dispatch
 * fails. Surfaced separately from `DatabaseError` so the admin UI can
 * distinguish "couldn't read the row" from "the workflow engine
 * rejected the dispatch".
 *
 * @since 1.0.0
 */
export class WorkflowDispatchFailedError extends Schema.TaggedError<WorkflowDispatchFailedError>()(
  "WorkflowDispatchFailedError",
  {
    installId: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Returned by `reissueInstallToken` when the install is in a status
 * that doesn't allow reissue — `active` (agent already pulled
 * credentials and is running, so a new agentSecret would silently
 * invalidate it) or `deleted`. Distinct from `ValidationError` so the
 * UI can surface a friendly status-aware copy.
 *
 * @since 1.0.0
 */
export class InstallReissueNotAllowed extends Schema.TaggedError<InstallReissueNotAllowed>()(
  "InstallReissueNotAllowed",
  {
    installId: Schema.String,
    status: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Returned by `reissueInstallToken` when the cipher is misconfigured
 * (e.g. missing `INSTALL_TOKEN_KEY`) or AES seal/unseal fails for some
 * other internal reason. Distinct from `DatabaseError` so the UI can
 * tell ops "rotate or check the env var" rather than "check the DB".
 *
 * @since 1.0.0
 */
export class InstallTokenCipherError extends Schema.TaggedError<InstallTokenCipherError>()(
  "InstallTokenCipherError",
  {
    installId: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Returned by `migrate` when the install cannot be migrated — a
 * `local-mac` deployment (migration is VPS-only) or a soft-deleted /
 * terminal row. Distinct from `RecordNotFoundError` so the admin UI can
 * render migration-specific copy.
 *
 * @since 1.0.0
 */
export class InstallNotMigratable extends Schema.TaggedError<InstallNotMigratable>()(
  "InstallNotMigratable",
  {
    installationId: Schema.String,
    status: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Returned by `migrate` when the enqueued `backup.now` command's final
 * `backup_snapshot` migration event is not observed within the bounded
 * wait. The migration aborts BEFORE the old server is deprovisioned so
 * the box (and its data) stay intact for a retry.
 *
 * @since 1.0.0
 */
export class MigrationSnapshotTimeout extends Schema.TaggedError<MigrationSnapshotTimeout>()(
  "MigrationSnapshotTimeout",
  {
    installationId: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Returned by `migrate` when the final `backup_snapshot` event reports a
 * non-`ok` outcome (restic failed, or backup was skipped because storage
 * is unconfigured / restic is unavailable). Aborts before deprovision —
 * there is no durable snapshot to restore from.
 *
 * @since 1.0.0
 */
export class MigrationSnapshotFailed extends Schema.TaggedError<MigrationSnapshotFailed>()(
  "MigrationSnapshotFailed",
  {
    installationId: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Returned by `migrate` when enqueuing the `backup.now` command onto the
 * install's mgmt-agent stream fails outright (Redis unreachable). Aborts
 * before deprovision.
 *
 * @since 1.0.0
 */
export class MigrationBackupEnqueueFailed extends Schema.TaggedError<MigrationBackupEnqueueFailed>()(
  "MigrationBackupEnqueueFailed",
  {
    installationId: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Returned by `migrate` when the OLD server is still visible at the cloud
 * provider after the bounded wait-until-gone poll that runs between
 * teardown and the re-provision dispatch. Cloud server destroys are
 * asynchronous (the provider accepts the DELETE and keeps the server
 * GET-visible for seconds-to-minutes), so the migration deliberately
 * blocks until the provider 404s the old `serverId` before dispatching —
 * otherwise the re-provision workflow's idempotent-reuse lookup would
 * "find" and reuse the dying server. On timeout the migration aborts and
 * NO re-provision is dispatched, so an operator is never told a migration
 * is in flight against a deleted box.
 *
 * @since 1.0.0
 */
export class MigrationOldServerStillPresent extends Schema.TaggedError<MigrationOldServerStillPresent>()(
  "MigrationOldServerStillPresent",
  {
    installationId: Schema.String,
    serverId: Schema.String,
    message: Schema.String,
  },
) {}

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Optional `customerId` filter for the list RPC. `include` opts
 * soft-deleted rows back into the response — the default excludes
 * any row whose `deletedAt` is set.
 * @since 1.0.0
 */
export const InstallsListInput = Schema.Struct({
  customerId: Schema.optional(Schema.String),
  include: Schema.optional(Schema.Literal("deleted")),
});

/**
 * Single-install lookup by id.
 * @since 1.0.0
 */
export const InstallsGetInput = Schema.Struct({
  id: Schema.String,
});

/**
 * Profile layer payload accepted on create. The admin UI may seed a
 * deterministic `base` layer and/or a human-authored `chosen` layer
 * at install-creation time.
 *
 * `data` is opaque JSONB — see `InstallationProfileLayer.data`.
 * @since 1.0.0
 */
export const InstallProfileLayerInput = Schema.Struct({
  data: Schema.Unknown,
});
export type InstallProfileLayerInput = Schema.Schema.Type<typeof InstallProfileLayerInput>;

/**
 * Form-side deployment type, distinguishing Mac (`local-mac`) from
 * cloud (`cloud-hetzner`). The handler maps `cloud-hetzner` to the
 * existing DB enum value `cloud-vps` when persisting.
 *
 * @since 1.0.0
 */
export const DeploymentTypeInput = Schema.Literal("local-mac", "cloud-hetzner");
export type DeploymentTypeInput = Schema.Schema.Type<typeof DeploymentTypeInput>;

/**
 * Create-install input. `region` and `size` map to
 * `agent_installations.location` and `agent_installations.serverType`.
 * `subdomain` is forwarded to the provisioning workflow for DNS
 * configuration but is not currently persisted.
 *
 * `deploymentType` selects between local-mac and cloud-hetzner; the
 * Infrastructure fields (`region`, `size`, `subdomain`) are only
 * meaningful on the `cloud-hetzner` branch and may be empty on the
 * `local-mac` branch. `channels` is the operator-driven multi-select
 * captured at intake; persisted as `installation_connections` rows.
 *
 * @since 1.0.0
 */
export const InstallsCreateInput = Schema.Struct({
  customerId: Schema.String,
  name: Schema.NonEmptyTrimmedString,
  deploymentType: DeploymentTypeInput,
  region: Schema.String,
  size: Schema.String,
  subdomain: Schema.String,
  channels: Schema.optionalWith(Schema.Array(Schema.String), { default: () => [] }),
  baseProfileLayer: Schema.optional(InstallProfileLayerInput),
  chosenProfileLayer: Schema.optional(InstallProfileLayerInput),
  // Which browser to provision (chrome | camoufox). Omitted = "chrome"
  // (persisted default). The `BrowserKind` allowlist rejects any off-list
  // value, so no free-string reaches provisioning.
  browser: Schema.optional(BrowserKind),
});

/**
 * Adopt-box input (CLA-778). Registers an EXISTING hand-managed box
 * (MAXClaw / Venture) for clawd monitoring WITHOUT provisioning anything.
 *
 *  - `name` — the node id / hostname the adopted box reports as.
 *  - `deploymentType` — the box's REAL OS (`local-mac` for a Mac, `cloud-vps`
 *    for a linux box). Persisted verbatim so every non-exhaustive
 *    `deploymentType` consumer (VPS provisioning, Hetzner deprovision, release
 *    binary selection) treats the row correctly — a Mac never gets served a
 *    linux binary. The explicit `adoptedAt` column, NOT a new deployment-type
 *    literal, is the adoption marker.
 *  - `tailscaleIp` / `publicIp` — optional network coordinates; default to
 *    empty strings (the same placeholder convention `createInstall` uses).
 *  - `customerId` — optional customer link (CLA-780). When set, the adopted box
 *    belongs to that customer: the row is inserted with `customerId` set and
 *    `seatBilling: null` (a BILLABLE seat on the next reconcile sweep), and the
 *    result carries a one-shot sealed install token instead of plaintext
 *    secrets. When absent, behavior is the CLA-778 snippet mode for our own
 *    hand-managed boxes.
 *
 * There is deliberately NO `notes` field — `agent_installations` has no such
 * column.
 *
 * @since 1.0.0
 */
export const InstallsAdoptInput = Schema.Struct({
  name: Schema.NonEmptyTrimmedString,
  deploymentType: DeploymentType,
  tailscaleIp: Schema.optional(Schema.String),
  publicIp: Schema.optional(Schema.String),
  customerId: Schema.optional(Schema.String),
});
export type InstallsAdoptInput = Schema.Schema.Type<typeof InstallsAdoptInput>;

/**
 * Adopt-box result. Two mutually-exclusive shapes keyed by the input's
 * `customerId`:
 *
 *  - Snippet mode (no `customerId`, CLA-778): `agentSecret` + `gatewayToken`
 *    are the plaintext credentials the operator drops into the install
 *    snippet's env, returned EXACTLY ONCE (stored encrypted-at-rest, never
 *    retrievable again). The token fields are omitted.
 *  - Customer mode (`customerId` set, CLA-780): `installToken` +
 *    `installTokenExpiresAt` carry the one-shot sealed install token (the
 *    same token+expiry precedent as `InstallsReissueInstallTokenResult`);
 *    the plaintext secrets are OMITTED — they travel only inside the sealed
 *    payload the customer redeems.
 *
 * Absent fields are omitted keys, never empty strings.
 *
 * @since 1.0.0
 */
export const InstallsAdoptResult = Schema.Struct({
  installationId: Schema.String,
  agentSecret: Schema.optional(Schema.String),
  gatewayToken: Schema.optional(Schema.String),
  installToken: Schema.optional(Schema.String),
  installTokenExpiresAt: Schema.optional(Schema.DateTimeUtc),
});
export type InstallsAdoptResult = Schema.Schema.Type<typeof InstallsAdoptResult>;

/**
 * Update-install input. Every field is optional — only the fields the
 * operator changes are sent. Validation rules are enforced server-side
 * (see `updateInstall`):
 *
 *  - `name` — must satisfy `isValidSlug` (lowercase alphanumeric + `-`,
 *    no leading/trailing hyphen, max 32 chars).
 *  - `status` — any value of `AgentInstallationStatus`. There is no
 *    transition guard (CLA-212 cancellation).
 *  - `subdomain` — written to `tailscaleHostname` (the placeholder host
 *    set at create time and updated by the provisioning workflow).
 *  - `region` / `size` — cosmetic post-provision; do NOT trigger a
 *    re-provision.
 *  - `deploymentType` — `local-mac` | `cloud-vps`.
 *  - `doDnsRecordId` — opaque DNS record id.
 *  - `installerConfigIssuedAt`, `paidAt`, `completedAt` — backfill /
 *    correction timestamps. Must NOT be in the future.
 *  - `rotateFrpToken` — pass `true` to mint a fresh ulid into
 *    `frpToken`. The operator does not supply the literal value.
 *
 * @since 1.0.0
 */
export const InstallsUpdateInput = Schema.Struct({
  id: Schema.String,
  name: Schema.optional(Schema.String),
  status: Schema.optional(AgentInstallationStatus),
  subdomain: Schema.optional(Schema.String),
  region: Schema.optional(Schema.String),
  size: Schema.optional(Schema.String),
  deploymentType: Schema.optional(DeploymentType),
  doDnsRecordId: Schema.optional(Schema.NullOr(Schema.String)),
  installerConfigIssuedAt: Schema.optional(Schema.NullOr(Schema.DateTimeUtc)),
  paidAt: Schema.optional(Schema.NullOr(Schema.DateTimeUtc)),
  completedAt: Schema.optional(Schema.NullOr(Schema.DateTimeUtc)),
  rotateFrpToken: Schema.optional(Schema.Boolean),
});
export type InstallsUpdateInput = Schema.Schema.Type<typeof InstallsUpdateInput>;

/**
 * Delete-install input. Admin soft-delete: sets status=deleted and
 * stamps deletedAt=now. Errors when the row is already deleted —
 * see {@link InvalidStateTransition}.
 *
 * @since 1.0.0
 */
export const InstallsDeleteInput = Schema.Struct({
  id: Schema.String,
});
export type InstallsDeleteInput = Schema.Schema.Type<typeof InstallsDeleteInput>;

/**
 * Deprovision-orchestrator input. The operator triggers this from the
 * admin Installs detail page. `reason` is persisted to
 * `agent_installations.deprovisionReason` so the admin UI can render
 * "why was this install torn down" alongside the row.
 *
 * @since 1.0.0
 */
export const InstallsDeprovisionInput = Schema.Struct({
  installationId: Schema.String,
  reason: Schema.String,
});
export type InstallsDeprovisionInput = Schema.Schema.Type<typeof InstallsDeprovisionInput>;

/**
 * Deprovision-orchestrator result. `errors` is the closed-union list
 * of sub-steps that did NOT complete cleanly — empty on a clean run,
 * non-empty when the row was advanced to `deprovisioned` despite
 * partial failures (the orchestrator surfaces those via
 * {@link DeprovisionPartialFailure} as well).
 *
 * @since 1.0.0
 */
export const InstallsDeprovisionResult = Schema.Struct({
  installationId: Schema.String,
  status: AgentInstallationStatus,
  deprovisionedAt: Schema.NullOr(Schema.DateTimeUtc),
  errors: Schema.Array(DeprovisionErrorTag),
});
export type InstallsDeprovisionResult = Schema.Schema.Type<typeof InstallsDeprovisionResult>;

/**
 * Provision-step input. The operator triggers this from the admin UI
 * after the install row has been created; the deployment branch is
 * read from `agent_installations.deployment_type` (defaulting to
 * `cloud-vps` when null, mirroring the public flow).
 *
 * @since 1.0.0
 */
export const InstallsProvisionInput = Schema.Struct({
  installId: Schema.String,
});
export type InstallsProvisionInput = Schema.Schema.Type<typeof InstallsProvisionInput>;

/**
 * Migrate-install input. Moves an existing cloud-vps install to a new
 * server (different `serverType`, datacenter, or `region`) with its
 * agent state restored from the latest restic snapshot.
 *
 * `region` / `serverType` are the NEW target. `skipFinalSnapshot` is the
 * disaster-recovery path — the old box is unreachable, so skip the
 * final `backup.now` + snapshot wait and restore the latest existing
 * snapshot instead.
 *
 * @since 1.0.0
 */
export const InstallsMigrateInput = Schema.Struct({
  installationId: Schema.String,
  region: Schema.String,
  serverType: Schema.String,
  skipFinalSnapshot: Schema.optional(Schema.Boolean),
});
export type InstallsMigrateInput = Schema.Schema.Type<typeof InstallsMigrateInput>;

/**
 * Migrate-install result. Reports which phases ran so the admin UI (and
 * CLA-543's migrate dialog) can render an accurate summary:
 *
 *  - `finalSnapshotAwaited` — false on the DR (`skipFinalSnapshot`) path.
 *  - `oldServerDeprovisioned` — always true once the orchestrator reaches
 *    the teardown (it advances regardless of best-effort step failures).
 *  - `deprovisionErrors` — best-effort teardown failures (frp/hetzner/dns).
 *  - `workflowDispatched` — the restore re-provision was dispatched.
 *
 * @since 1.0.0
 */
export const InstallsMigrateResult = Schema.Struct({
  installationId: Schema.String,
  finalSnapshotAwaited: Schema.Boolean,
  oldServerDeprovisioned: Schema.Boolean,
  deprovisionErrors: Schema.Array(DeprovisionErrorTag),
  workflowDispatched: Schema.Boolean,
});
export type InstallsMigrateResult = Schema.Schema.Type<typeof InstallsMigrateResult>;

/**
 * Upgrade-OpenClaw input — the canary trigger for a fleet core bump
 * (CLA-993). Enqueues one `openclaw.upgrade` command for a single install
 * after validating `targetVersion` against the shared version policy.
 *
 * @since 1.0.0
 */
export const InstallsUpgradeOpenclawInput = Schema.Struct({
  installationId: Schema.String,
  targetVersion: Schema.String,
});
export type InstallsUpgradeOpenclawInput = Schema.Schema.Type<typeof InstallsUpgradeOpenclawInput>;

/**
 * Upgrade/rollback-OpenClaw result — the enqueued `commandId` plus the
 * from/to version pair the command carries (CLA-993). For an upgrade,
 * `previousVersion` is the box's current `ocVersion` and `targetVersion` the
 * requested target. For a rollback, `previousVersion` is the version currently
 * running (the last outcome's `to`) and `targetVersion` the version being
 * reinstated (the last outcome's `from`). The actual outcome is read back from
 * the `openclaw_upgrade` event on the install's config log.
 *
 * @since 1.0.0
 */
export const InstallsUpgradeOpenclawResult = Schema.Struct({
  installationId: Schema.String,
  commandId: Schema.String,
  previousVersion: Schema.String,
  targetVersion: Schema.String,
});
export type InstallsUpgradeOpenclawResult = Schema.Schema.Type<typeof InstallsUpgradeOpenclawResult>;

/**
 * Rollback-OpenClaw input (CLA-993). Enqueues one `openclaw.rollback`
 * command that reinstates the `from` version of the newest terminal
 * `openclaw_upgrade` outcome on the install's config log.
 *
 * @since 1.0.0
 */
export const InstallsRollbackOpenclawInput = Schema.Struct({
  installationId: Schema.String,
});
export type InstallsRollbackOpenclawInput = Schema.Schema.Type<typeof InstallsRollbackOpenclawInput>;

/**
 * Reissue-install-token input. The operator triggers this from the
 * Mac actions block on the install detail page when the previous
 * curl was burned (run once successfully, run prematurely, etc.) and
 * the customer needs a fresh one. Cloud installs ignore this RPC.
 *
 * @since 1.0.0
 */
export const InstallsReissueInstallTokenInput = Schema.Struct({
  installId: Schema.String,
});
export type InstallsReissueInstallTokenInput = Schema.Schema.Type<typeof InstallsReissueInstallTokenInput>;

/**
 * Reissue-install-token result. Returns the new lookup token and its
 * expiry so the UI can render the new curl immediately without
 * waiting for the detail RPC to refetch.
 *
 * @since 1.0.0
 */
export const InstallsReissueInstallTokenResult = Schema.Struct({
  installId: Schema.String,
  installToken: Schema.String,
  installTokenExpiresAt: Schema.DateTimeUtc,
});
export type InstallsReissueInstallTokenResult = Schema.Schema.Type<typeof InstallsReissueInstallTokenResult>;

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * Compact install row for the list view.
 *
 * `subscriptionStatus` is the joined `subscriptions.status` for the
 * latest subscription linked to this install, or null when none.
 *
 * @since 1.0.0
 */
export const InstallSummary = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  customerId: Schema.NullOr(Schema.String),
  region: Schema.String,
  size: Schema.String,
  host: Schema.String,
  status: AgentInstallationStatus,
  installFeeStatus: InstallFeeStatus,
  installFeeAmount: Schema.NullOr(Schema.Int),
  // Stripe's hosted invoice URL captured when `sendInstallInvoice`
  // finalises the invoice. Surfaced in the admin UI as a "View
  // invoice" link so admins can copy/share it manually — Stripe
  // sandbox / test mode never emails customers regardless of the
  // dashboard toggle, and the toggle isn't exposed via API anyway.
  installInvoiceUrl: Schema.NullOr(Schema.String),
  managedOffered: Schema.Boolean,
  subscriptionStatus: Schema.NullOr(SubscriptionStatus),
  createdAt: Schema.DateTimeUtc,
  // Surfaced so the list-page row actions can branch on Mac vs. cloud
  // and render the curl-copy + reissue buttons inline (CLA-241).
  // Cloud rows render a plain "Open detail" link instead.
  deploymentType: Schema.NullOr(DeploymentType),
  installToken: Schema.NullOr(Schema.String),
});
export type InstallSummary = Schema.Schema.Type<typeof InstallSummary>;

/**
 * Profile-layer projection on the detail view.
 * @since 1.0.0
 */
export const InstallProfileLayer = Schema.Struct({
  id: Schema.String,
  kind: Schema.Literal("base", "chosen"),
  data: Schema.Unknown,
  version: Schema.Number,
  authorId: Schema.NullOr(Schema.String),
  createdAt: Schema.DateTimeUtc,
});
export type InstallProfileLayer = Schema.Schema.Type<typeof InstallProfileLayer>;

/**
 * Provisioning event projection on the detail view.
 * @since 1.0.0
 */
export const InstallProvisioningEvent = Schema.Struct({
  id: Schema.String,
  seq: Schema.Number,
  step: Schema.String,
  status: Schema.String,
  message: Schema.NullOr(Schema.String),
  percent: Schema.NullOr(Schema.Number),
  occurredAt: Schema.DateTimeUtc,
});
export type InstallProvisioningEvent = Schema.Schema.Type<typeof InstallProvisioningEvent>;

/**
 * Subscription projection on the detail view.
 * @since 1.0.0
 */
export const InstallSubscription = Schema.Struct({
  id: Schema.String,
  customerId: Schema.String,
  installationId: Schema.NullOr(Schema.String),
  plan: Schema.String,
  status: SubscriptionStatus,
  amount: Schema.NullOr(Schema.Number),
  currentPeriodStart: Schema.NullOr(Schema.DateTimeUtc),
  currentPeriodEnd: Schema.NullOr(Schema.DateTimeUtc),
  cancelAtPeriodEnd: Schema.Boolean,
});
export type InstallSubscription = Schema.Schema.Type<typeof InstallSubscription>;

/**
 * Activity-feed event type discriminator. Five sources are
 * unified into a single reverse-chrono stream:
 *  - `provisioning` — rows from `onboarding_provisioning_events`
 *  - `invoice` — synthesized from `install_invoiced_at` /
 *    `install_paid_at` columns on `agent_installations`
 *  - `subscription` — synthesized from `subscriptions.createdAt` /
 *    `updatedAt` (with `canceled` derived from `status`)
 *  - `terminal` — admin terminal-session activity on the box
 *  - `backup` — backup snapshot / backup-restore events reported by the
 *    on-node agent (list refreshes, restore requests and outcomes)
 *
 * @since 1.0.0
 */
export const InstallActivityEventType = Schema.Literal(
  "provisioning",
  "invoice",
  "subscription",
  "terminal",
  "backup",
);
export type InstallActivityEventType = Schema.Schema.Type<typeof InstallActivityEventType>;

/**
 * Public projection for a single activity-feed row.
 *
 * `metadata` carries source-specific context (step/status, invoice id,
 * subscription plan, etc.) and is opaque to the UI; the activity feed
 * renders it as expandable JSON.
 *
 * @since 1.0.0
 */
export const InstallActivityEvent = Schema.Struct({
  timestamp: Schema.DateTimeUtc,
  type: InstallActivityEventType,
  summary: Schema.String,
  metadata: Schema.optional(Schema.Unknown),
});
export type InstallActivityEvent = Schema.Schema.Type<typeof InstallActivityEvent>;

/**
 * Activity-feed query input. `limit` defaults server-side; `before`
 * is a UTC timestamp cursor for keyset pagination.
 *
 * @since 1.0.0
 */
export const InstallsActivityInput = Schema.Struct({
  installId: Schema.String,
  limit: Schema.optional(Schema.Int),
  before: Schema.optional(Schema.DateTimeUtc),
});
export type InstallsActivityInput = Schema.Schema.Type<typeof InstallsActivityInput>;

/**
 * Activity-feed streaming input. The streaming RPC subscribes the
 * caller to live provisioning events for the given install via
 * {@link ProvisioningEventHub} — backfill replay first, live tail
 * after, terminating on a `done` or `failed` event.
 *
 * Mirror of {@link InstallsActivityInput} without the pagination
 * fields, since the stream is unbounded by definition.
 *
 * @since 1.0.0
 */
export const InstallsStreamActivityInput = Schema.Struct({
  installId: Schema.String,
});
export type InstallsStreamActivityInput = Schema.Schema.Type<typeof InstallsStreamActivityInput>;

/**
 * Provision-step result. Discriminated by `deploymentType` so the
 * admin UI can render different post-action affordances:
 *
 *  - `cloud-vps` — workflow has been dispatched server-side; the UI
 *    should redirect to the activity feed and watch for events.
 *  - `local-mac` — the customer needs to run the curl snippet on
 *    their Mac. The token, expiry, and curl command are returned so
 *    the operator can copy/share them. No workflow is dispatched.
 *
 * `installerToken`, `installerTokenExpiresAt`, `curlCommand`, and
 * `binaryBaseUrl` are populated on the `local-mac` branch and null
 * on the `cloud-vps` branch. `workflowDispatched` is true on
 * `cloud-vps` and false on `local-mac`.
 *
 * @since 1.0.0
 */
export const InstallsProvisionResult = Schema.Struct({
  installId: Schema.String,
  deploymentType: DeploymentType,
  workflowDispatched: Schema.Boolean,
  installerToken: Schema.NullOr(Schema.String),
  installerTokenExpiresAt: Schema.NullOr(Schema.String),
  curlCommand: Schema.NullOr(Schema.String),
  binaryBaseUrl: Schema.NullOr(Schema.String),
});
export type InstallsProvisionResult = Schema.Schema.Type<typeof InstallsProvisionResult>;

/**
 * Install detail — summary fields plus profile layers, recent
 * provisioning events, and the linked subscription.
 *
 * @since 1.0.0
 */
export const InstallDetail = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  customerId: Schema.NullOr(Schema.String),
  region: Schema.String,
  size: Schema.String,
  host: Schema.String,
  status: AgentInstallationStatus,
  installFeeStatus: InstallFeeStatus,
  installFeeAmount: Schema.NullOr(Schema.Int),
  installInvoiceUrl: Schema.NullOr(Schema.String),
  managedOffered: Schema.Boolean,
  subscriptionStatus: Schema.NullOr(SubscriptionStatus),
  createdAt: Schema.DateTimeUtc,
  deploymentType: Schema.NullOr(DeploymentType),
  installToken: Schema.NullOr(Schema.String),
  installTokenExpiresAt: Schema.NullOr(Schema.DateTimeUtc),
  agentVersion: Schema.NullOr(Schema.String),
  // CLA-320: OpenClaw version reported by clawd's local `openclaw health --json`
  // probe, surfaced next to the clawd `agentVersion` so admin can see the box's
  // OpenClaw version across a multi-version fleet.
  ocVersion: Schema.NullOr(Schema.String),
  agentVersionPin: Schema.NullOr(Schema.String),
  updateChannelOverride: Schema.NullOr(AgentChannelName),
  lastUpdateCheckAt: Schema.NullOr(Schema.DateTimeUtc),
  lastUpdateOutcome: Schema.NullOr(Schema.String),
  updateTargetVersion: Schema.NullOr(Schema.String),
  // HEALTH-1: latest `openclaw health --json` snapshot the agent has
  // reported via heartbeat. `null` until the install reports its first
  // probe cycle; the admin install detail page renders a green/yellow/
  // red badge from these three fields.
  ocHealthOk: Schema.NullOr(Schema.Boolean),
  ocHealthObservedAt: Schema.NullOr(Schema.DateTimeUtc),
  ocHealthRaw: Schema.NullOr(OcHealthSnapshot),
  // CLA-775: latest compact DoctorLoop assessment. `null` until the box
  // reports its first assessment cycle. Its `status` is the first surface on
  // which admin ever sees `"degraded"` — `ocHealthOk` stays ok/down-only.
  ocAssessment: Schema.NullOr(OcAssessmentSummary),
  // CLA-782: latest redacted OpenClaw config snapshot reported by clawd on
  // its own RPC (sibling issue CLA-807). All three are `null` until the box
  // reports its first snapshot — the normal state until the sibling ships.
  // `ocConfigRedacted` is customer-shaped and opaque (every secret-bearing
  // value is fingerprinted on the box), hence `Schema.NullOr(Schema.Unknown)`
  // — same precedent as install-logs' `meta`. Read-only surface: the server
  // never pushes config back to a box.
  ocConfigRedacted: Schema.NullOr(Schema.Unknown),
  ocConfigHash: Schema.NullOr(Schema.String),
  ocConfigCapturedAt: Schema.NullOr(Schema.DateTimeUtc),
  // CLA-784: remote-support consent as last reported by the box's heartbeat
  // (the ONLY writer — there is no server-side enable path). Surfaced so the
  // admin agent detail page renders an enabled/disabled badge and support
  // staff can interpret a refusal once the dispatch guard sibling ships.
  remoteSupportEnabled: Schema.Boolean,
  remoteSupportChangedAt: Schema.NullOr(Schema.DateTimeUtc),
  // CLA-543: latest restic `backup_snapshot` migration event surfaced so
  // the admin agent detail page can render a backup-health badge. Both are
  // `null` until the box reports its first backup cycle ("never backed
  // up"). `lastBackupStatus` is `"applied"` when the snapshot completed
  // (`payload.status === "ok"`) and `"failed"` for any other outcome
  // (restic failure OR a skipped run — neither yields a durable snapshot).
  lastBackupAt: Schema.NullOr(Schema.DateTimeUtc),
  lastBackupStatus: Schema.NullOr(Schema.Literal("applied", "failed")),
  profileLayers: Schema.Array(InstallProfileLayer),
  provisioningEvents: Schema.Array(InstallProvisioningEvent),
  subscription: Schema.NullOr(InstallSubscription),
  // CLA-271: surfaced so the admin install detail header can render
  // "why was this torn down" alongside the row. `deprovisionReason`
  // is NULL when the agent's self-callback webhook (CLA-269) raced the
  // operator-initiated deprovision RPC — the agent writes status +
  // deprovisionedAt but not the reason — so the UI renders NULL as
  // "agent-initiated".
  deprovisionedAt: Schema.NullOr(Schema.DateTimeUtc),
  deprovisionReason: Schema.NullOr(Schema.String),
});
export type InstallDetail = Schema.Schema.Type<typeof InstallDetail>;

/**
 * Hetzner location projection for the admin New Install form's region
 * dropdown.
 *
 * @since 1.0.0
 */
export const HetznerLocationOption = Schema.Struct({
  id: Schema.Number,
  name: Schema.String,
  city: Schema.String,
  country: Schema.String,
});
export type HetznerLocationOption = Schema.Schema.Type<typeof HetznerLocationOption>;

/**
 * Hetzner server-type projection for the admin New Install form's size
 * dropdown. Prices are gross EUR pulled from the SKU's first listed
 * location.
 *
 * @since 1.0.0
 */
export const HetznerServerTypeOption = Schema.Struct({
  id: Schema.Number,
  name: Schema.String,
  cores: Schema.Number,
  memory: Schema.Number,
  disk: Schema.Number,
  priceMonthly: Schema.Number,
  priceHourly: Schema.Number,
});
export type HetznerServerTypeOption = Schema.Schema.Type<typeof HetznerServerTypeOption>;

/**
 * Combined Hetzner catalog payload returned by the
 * `admin/installs/hetznerOptions` RPC.
 *
 * @since 1.0.0
 */
export const HetznerOptions = Schema.Struct({
  locations: Schema.Array(HetznerLocationOption),
  serverTypes: Schema.Array(HetznerServerTypeOption),
});
export type HetznerOptions = Schema.Schema.Type<typeof HetznerOptions>;

/**
 * DigitalOcean region projection for the admin Add Agent form's region
 * dropdown. Simpler than the Hetzner shape — DigitalOcean's static curated
 * catalog only carries a region slug and its human name.
 *
 * @since 1.0.0
 */
export const DigitalOceanRegionOption = Schema.Struct({
  slug: Schema.String,
  name: Schema.String,
});
export type DigitalOceanRegionOption = Schema.Schema.Type<typeof DigitalOceanRegionOption>;

/**
 * DigitalOcean catalog payload returned by the
 * `admin/installs/digitalOceanOptions` RPC. Served from the static curated
 * `DO_REGIONS` constant — NOT the deferred live `/v2/regions` catalog.
 *
 * @since 1.0.0
 */
export const DigitalOceanOptions = Schema.Struct({
  regions: Schema.Array(DigitalOceanRegionOption),
});
export type DigitalOceanOptions = Schema.Schema.Type<typeof DigitalOceanOptions>;

/**
 * OVH region projection for the admin Add Agent form's region dropdown.
 * Mirrors the DigitalOcean shape — OVH's static curated datacenter catalog
 * only carries a datacenter slug and its human name.
 *
 * @since 1.0.0
 */
export const OvhRegionOption = Schema.Struct({
  slug: Schema.String,
  name: Schema.String,
});
export type OvhRegionOption = Schema.Schema.Type<typeof OvhRegionOption>;

/**
 * OVH catalog payload returned by the `admin/installs/ovhOptions` RPC. Served
 * from the static curated `OVH_DATACENTERS` constant.
 *
 * @since 1.0.0
 */
export const OvhOptions = Schema.Struct({
  regions: Schema.Array(OvhRegionOption),
});
export type OvhOptions = Schema.Schema.Type<typeof OvhOptions>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Installs RPC group.
 *
 * All endpoints require admin role via `AdminMiddleware`.
 * Tags are namespaced under `admin/installs/`.
 *
 * @since 1.0.0
 */
export class InstallsRpcs extends RpcGroup.make(
  Rpc.make("list", {
    payload: InstallsListInput,
    success: Schema.Array(InstallSummary),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("get", {
    payload: InstallsGetInput,
    success: InstallDetail,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("create", {
    payload: InstallsCreateInput,
    success: InstallSummary,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      DatabaseError,
      InstallAlreadyProvisionedError,
      InstallTokenCipherError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("adopt", {
    payload: InstallsAdoptInput,
    success: InstallsAdoptResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      DatabaseError,
      InstallAlreadyProvisionedError,
      RecordNotFoundError,
      InstallTokenCipherError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("update", {
    payload: InstallsUpdateInput,
    success: InstallSummary,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      ValidationError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("delete", {
    payload: InstallsDeleteInput,
    success: Schema.Void,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      DatabaseError,
      RecordNotFoundError,
      InvalidStateTransition,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("provision", {
    payload: InstallsProvisionInput,
    success: InstallsProvisionResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      WorkflowDispatchFailedError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("deprovision", {
    payload: InstallsDeprovisionInput,
    success: InstallsDeprovisionResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      InstallNotDeprovisionable,
      DeprovisionPartialFailure,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("reissueInstallToken", {
    payload: InstallsReissueInstallTokenInput,
    success: InstallsReissueInstallTokenResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      InstallReissueNotAllowed,
      InstallTokenCipherError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("migrate", {
    payload: InstallsMigrateInput,
    success: InstallsMigrateResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      InstallNotMigratable,
      RemoteSupportNotEnabled,
      MigrationBackupEnqueueFailed,
      MigrationSnapshotTimeout,
      MigrationSnapshotFailed,
      MigrationOldServerStillPresent,
      WorkflowDispatchFailedError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("upgradeOpenclaw", {
    payload: InstallsUpgradeOpenclawInput,
    success: InstallsUpgradeOpenclawResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      RemoteSupportNotEnabled,
      OpenclawUpgradeRefused,
      ValkeyError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("rollbackOpenclaw", {
    payload: InstallsRollbackOpenclawInput,
    success: InstallsUpgradeOpenclawResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      DatabaseError,
      RemoteSupportNotEnabled,
      OpenclawUpgradeRefused,
      ValkeyError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("activity", {
    payload: InstallsActivityInput,
    success: Schema.Array(InstallActivityEvent),
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("streamActivity", {
    payload: InstallsStreamActivityInput,
    success: InstallActivityEvent,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
    stream: true,
  }).middleware(AdminMiddleware),
  Rpc.make("hetznerOptions", {
    success: HetznerOptions,
    error: Schema.Union(Unauthorized, Forbidden, HetznerCatalogRpcError),
  }).middleware(AdminMiddleware),
  Rpc.make("digitalOceanOptions", {
    success: DigitalOceanOptions,
    error: Schema.Union(Unauthorized, Forbidden),
  }).middleware(AdminMiddleware),
  Rpc.make("ovhOptions", {
    success: OvhOptions,
    error: Schema.Union(Unauthorized, Forbidden),
  }).middleware(AdminMiddleware),
).prefix("admin/installs/") {}
